import React, { useCallback, useEffect, useState } from 'react';
import Nango from '@nangohq/frontend';
import {
  CheckCircle2, Clock, AlertTriangle, XCircle,
  RefreshCw, ExternalLink, Plug, Unplug, Zap,
  MessageCircle, Mail, Play, Instagram, Globe,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CONNECTIONS = [
  {
    id: 'meta',
    label: 'Meta (Facebook + Instagram)',
    nango: 'facebook',
    icon: '📘',
    color: '#1877F2',
    desc: 'Publica en Facebook e Instagram directamente desde el panel',
    docs: 'https://developers.facebook.com',
  },
  {
    id: 'instagram',
    label: 'Instagram Business',
    nango: 'instagram',
    icon: '📸',
    color: '#E1306C',
    desc: 'Conecta tu cuenta profesional de Instagram para publicar contenido',
    docs: 'https://developers.facebook.com/docs/instagram-api',
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    nango: 'tiktok',
    icon: '🎵',
    color: '#010101',
    desc: 'Publica vídeos en TikTok automáticamente con Aynara',
    docs: 'https://developers.tiktok.com',
  },
  {
    id: 'google',
    label: 'Google (Drive + Docs)',
    nango: 'google-drive',
    icon: '🔷',
    color: '#4285F4',
    desc: 'Accede a Google Drive y exporta documentos automáticamente',
    docs: 'https://developers.google.com',
  },
  {
    id: 'gmail',
    label: 'Gmail',
    nango: 'google-mail',
    icon: '✉️',
    color: '#EA4335',
    desc: 'Envía emails directamente desde tu cuenta Gmail',
    docs: 'https://developers.google.com/gmail',
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp Business (Nango)',
    nango: 'whatsapp-business',
    icon: '💬',
    color: '#25D366',
    desc: 'Conecta tu número de WhatsApp Business via OAuth',
    docs: 'https://docs.nango.dev/integrations/whatsapp',
  },
];

const STATUS_STYLES = {
  connected: { bg: 'bg-emerald-500/15 border-emerald-500/30', text: 'text-emerald-400', label: 'Conectado', Icon: CheckCircle2 },
  error: { bg: 'bg-red-500/15 border-red-500/30', text: 'text-red-400', label: 'Error', Icon: AlertTriangle },
  pending: { bg: 'bg-amber-500/15 border-amber-500/30', text: 'text-amber-400', label: 'Pendiente', Icon: Clock },
  disconnected: { bg: 'bg-slate-700/30 border-slate-600/30', text: 'text-slate-500', label: 'No conectado', Icon: XCircle },
};

function getStatus(statusDoc) {
  if (!statusDoc) return 'disconnected';
  return statusDoc.status || 'disconnected';
}

export default function AdminConnectionsPage({ authFetch }) {
  const [connStatus, setConnStatus] = useState({});
  const [loading, setLoading] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const fetchStatus = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await authFetch(`${API}/api/admin/connections/status`);
      setConnStatus(data || {});
    } catch (e) {
      showToast('No se pudo cargar el estado de conexiones');
    } finally {
      setRefreshing(false);
    }
  }, [authFetch]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const connectNango = async (conn) => {
    setLoading(l => ({ ...l, [conn.id]: true }));
    try {
      const res = await authFetch(`${API}/api/admin/connections/nango/session`, {
        method: 'POST',
        body: JSON.stringify({ integration: conn.nango }),
      });
      if (!res?.session_token) throw new Error('No se pudo iniciar sesión Nango');

      const nango = new Nango();
      const connectUi = nango.openConnectUI({
        onEvent: async (event) => {
          if (event.type === 'close') {
            setLoading(l => ({ ...l, [conn.id]: false }));
            fetchStatus();
          }
          if (event.type === 'connect') {
            try {
              await authFetch(`${API}/api/admin/connections/nango/callback`, {
                method: 'POST',
                body: JSON.stringify({ integration: conn.nango, connection_id: event.payload?.connectionId || event.payload?.connection_id }),
              });
              showToast(`${conn.label} conectado correctamente`);
            } catch (e) {
              showToast(`Error guardando conexión: ${e.message}`);
            }
            setLoading(l => ({ ...l, [conn.id]: false }));
            fetchStatus();
          }
        },
      });
      connectUi.setSessionToken(res.session_token);
    } catch (e) {
      showToast(`Error: ${e.message}`);
      setLoading(l => ({ ...l, [conn.id]: false }));
    }
  };

  const disconnect = async (conn) => {
    if (!window.confirm(`¿Desconectar ${conn.label}?`)) return;
    setLoading(l => ({ ...l, [conn.id]: true }));
    try {
      await authFetch(`${API}/api/admin/connections/nango/${conn.id}`, { method: 'DELETE' });
      showToast(`${conn.label} desconectado`);
      fetchStatus();
    } catch (e) {
      showToast(`Error: ${e.message}`);
    } finally {
      setLoading(l => ({ ...l, [conn.id]: false }));
    }
  };

  const connected = CONNECTIONS.filter(c => getStatus(connStatus[c.id]) === 'connected').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Conexiones Un Clic</h2>
          <p className="text-slate-400 text-sm mt-1">
            {connected}/{CONNECTIONS.length} canales conectados — OAuth seguro vía Nango
          </p>
        </div>
        <button
          data-testid="connections-refresh-btn"
          onClick={fetchStatus}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Progress bar */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4 flex items-center gap-4">
        <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#00d4ff] to-emerald-400 rounded-full transition-all duration-700"
            style={{ width: `${(connected / CONNECTIONS.length) * 100}%` }}
          />
        </div>
        <span className="text-slate-400 text-sm font-medium whitespace-nowrap">
          {connected} de {CONNECTIONS.length}
        </span>
      </div>

      {/* Connection cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        {CONNECTIONS.map(conn => {
          const statusData = connStatus[conn.id];
          const status = getStatus(statusData);
          const { bg, text, label, Icon } = STATUS_STYLES[status] || STATUS_STYLES.disconnected;
          const isLoading = loading[conn.id];

          return (
            <div
              key={conn.id}
              data-testid={`connection-card-${conn.id}`}
              className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 flex flex-col gap-4 hover:border-slate-700 transition-colors"
            >
              {/* Top row */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: `${conn.color}20`, border: `1px solid ${conn.color}30` }}
                  >
                    {conn.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm leading-tight">{conn.label}</h3>
                    <p className="text-slate-500 text-xs mt-0.5">{conn.desc}</p>
                  </div>
                </div>
              </div>

              {/* Status badge */}
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium w-fit ${bg} ${text}`}>
                <Icon className="w-3.5 h-3.5" />
                {label}
                {statusData?.display && status === 'connected' && (
                  <span className="opacity-70">· {statusData.display}</span>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 mt-auto">
                {status === 'connected' ? (
                  <>
                    <button
                      data-testid={`disconnect-btn-${conn.id}`}
                      onClick={() => disconnect(conn)}
                      disabled={isLoading}
                      className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
                    >
                      <Unplug className="w-3.5 h-3.5" />
                      {isLoading ? 'Procesando...' : 'Desconectar'}
                    </button>
                    <a
                      href={conn.docs}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 border border-slate-700 text-slate-400 rounded-xl text-xs hover:bg-slate-700 transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Docs
                    </a>
                  </>
                ) : (
                  <button
                    data-testid={`connect-btn-${conn.id}`}
                    onClick={() => connectNango(conn)}
                    disabled={isLoading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
                    style={{
                      background: `${conn.color}20`,
                      border: `1px solid ${conn.color}40`,
                      color: conn.color,
                    }}
                  >
                    <Plug className="w-3.5 h-3.5" />
                    {isLoading ? (
                      <span className="flex items-center gap-1">
                        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Conectando...
                      </span>
                    ) : 'Conectar 1 clic'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* WhatsApp Direct API status */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-[#25D366]/15 border border-[#25D366]/25 flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-[#25D366]" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">WhatsApp Cloud API (Directo)</h3>
            <p className="text-slate-500 text-xs">Token de Sistema Meta — ya configurado</p>
          </div>
          <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-medium rounded-lg">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Activo
          </div>
        </div>
        <p className="text-slate-500 text-xs">
          Token System User permanente configurado. Para enviar a números reales, activa el modo LIVE en{' '}
          <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#00d4ff] hover:underline">
            Meta Developer Console
          </a>
        </p>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 text-white text-sm px-5 py-3 rounded-xl shadow-xl z-50 animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}
